/**
 * Client Services Page - Microsoft 365 Style
 * Manage services assigned to clients
 */

import React, { useEffect, useState, useMemo } from 'react';
import { useNavigate } from 'react-router-dom';
import {
  makeStyles,
  Text,
  Badge,
  Button,
  Menu,
  MenuTrigger,
  MenuPopover,
  MenuList,
  MenuItem,
  Dialog,
  DialogSurface,
  DialogTitle,
  DialogBody,
  DialogActions,
  DialogContent,
  Input,
  Field,
} from '@fluentui/react-components';
import {
  EditRegular,
  DeleteRegular,
  MoreHorizontalRegular,
  CalendarRegular,
  FilterRegular,
  DismissRegular,
} from '@fluentui/react-icons';
import { ContentCard, TableCommandBar } from '../../../components/layout';
import { SearchableDropdown } from '../../../components/common';
import { ApiClient, newToast, fullDate } from '../../../configurations/index.config';
import { getErrorMessage } from '../../../types';

const useStyles = makeStyles({
  pageHeader: {
    marginBottom: '24px',
  },
  pageTitle: {
    fontSize: '24px',
    fontWeight: '600',
    color: '#323130',
    marginBottom: '4px',
    display: 'block',
  },
  pageSubtitle: {
    fontSize: '14px',
    color: '#605e5c',
    display: 'block',
  },
  filterBar: {
    marginBottom: '16px',
    display: 'flex',
    gap: '12px',
    alignItems: 'flex-end',
  },
  filters: {
    display: 'flex',
    flexDirection: 'column',
    gap: '12px',
    padding: '16px',
    backgroundColor: '#faf9f8',
    borderRadius: '8px',
    marginBottom: '16px',
  },
  filterRow: {
    display: 'flex',
    gap: '12px',
    alignItems: 'flex-end',
    flexWrap: 'wrap',
  },
  filterDropdown: {
    flex: '1 1 200px',
    minWidth: '150px',
  },
  filterDate: {
    flex: '1 1 150px',
    minWidth: '140px',
  },
  filterHeader: {
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'space-between',
    marginBottom: '8px',
  },
  filterTitle: {
    display: 'flex',
    alignItems: 'center',
    gap: '8px',
    fontSize: '14px',
    fontWeight: '600',
    color: '#323130',
  },
  table: {
    width: '100%',
    borderCollapse: 'collapse',
  },
  tableHeader: {
    backgroundColor: '#f5f5f5',
    borderBottom: '2px solid #e1dfdd',
  },
  th: {
    padding: '12px 16px',
    textAlign: 'left',
    fontSize: '13px',
    fontWeight: '600',
    color: '#323130',
  },
  tr: {
    borderBottom: '1px solid #e1dfdd',
    ':hover': {
      backgroundColor: '#f9f9f9',
    },
  },
  td: {
    padding: '12px 16px',
    fontSize: '14px',
    color: '#323130',
  },
  emptyState: {
    padding: '60px 20px',
    textAlign: 'center',
    color: '#605e5c',
  },
  actionButton: {
    minWidth: 'auto',
    padding: '6px',
  },
});

export const ClientServicesFluentUI: React.FC = () => {
  const styles = useStyles();
  const navigate = useNavigate();
  const [clientServices, setClientServices] = useState<any[]>([]);
  const [clients, setClients] = useState<any[]>([]);
  const [selectedClient, setSelectedClient] = useState('');
  const [loading, setLoading] = useState(false);
  const [deleteDialogOpen, setDeleteDialogOpen] = useState(false);
  const [selectedService, setSelectedService] = useState<any>(null);

  // Filter states
  const [filterService, setFilterService] = useState('all');
  const [filterSite, setFilterSite] = useState('all');
  const [filterStartDate, setFilterStartDate] = useState('');
  const [filterEndDate, setFilterEndDate] = useState('');

  useEffect(() => {
    fetchClients();
  }, []);

  useEffect(() => {
    if (selectedClient) {
      fetchClientServices();
      // Reset filters when client changes
      setFilterService('all');
      setFilterSite('all');
      setFilterStartDate('');
      setFilterEndDate('');
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [selectedClient]); // Fetch when client changes

  const fetchClients = async () => {
    try {
      const response = await ApiClient.get('/get-clients');
      if (response.data.status === 'success') {
        setClients(response.data.data || []);
      }
    } catch (error: unknown) {
      newToast('error', getErrorMessage(error));
    }
  };

  // Extract unique services from client services data
  const uniqueServices = useMemo(() => {
    const serviceMap = new Map();
    clientServices.forEach(cs => {
      if (cs.service_id && cs.service_name && !serviceMap.has(cs.service_id)) {
        serviceMap.set(cs.service_id, { id: cs.service_id, service_name: cs.service_name });
      }
    });
    return Array.from(serviceMap.values());
  }, [clientServices]);

  // Extract unique sites from client services data
  const uniqueSites = useMemo(() => {
    const siteMap = new Map();
    clientServices.forEach(cs => {
      if (cs.site_id && cs.site_name && !siteMap.has(cs.site_id)) {
        siteMap.set(cs.site_id, { id: cs.site_id, site_name: cs.site_name });
      }
    });
    return Array.from(siteMap.values());
  }, [clientServices]);

  const fetchClientServices = async () => {
    if (!selectedClient) {
      return;
    }
    setLoading(true);
    try {
      const response = await ApiClient.get('/get-client-services', {
        params: { client_id: selectedClient },
      });
      if (response.data.status === 'success') {
        setClientServices(response.data.data || []);
      }
    } catch (error: unknown) {
      newToast('error', getErrorMessage(error));
    }
    setLoading(false);
  };

  const handleDeleteService = async () => {
    if (!selectedService) {
      return;
    }
    try {
      const response = await ApiClient.post('/delete-client-service', {
        id: selectedService.id,
      });
      if (response.data.status === 'success') {
        newToast('success', 'Service deleted successfully');
        fetchClientServices();
      } else {
        newToast('error', response.data?.message || 'Failed to delete service');
      }
    } catch (error: unknown) {
      newToast('error', getErrorMessage(error));
    }
    setDeleteDialogOpen(false);
    setSelectedService(null);
  };

  // Filter client services
  const filteredClientServices = useMemo(() => {
    return clientServices.filter(service => {
      // Filter by service
      if (filterService !== 'all' && service.service_id?.toString() !== filterService) {
        return false;
      }

      // Filter by site
      if (filterSite !== 'all' && service.site_id?.toString() !== filterSite) {
        return false;
      }

      // Filter by start date
      if (filterStartDate && service.start_date) {
        const serviceStart = new Date(service.start_date);
        const filterStart = new Date(filterStartDate);
        if (serviceStart < filterStart) {
          return false;
        }
      }

      // Filter by end date
      if (filterEndDate && service.start_date) {
        const serviceStart = new Date(service.start_date);
        const filterEnd = new Date(filterEndDate);
        if (serviceStart > filterEnd) {
          return false;
        }
      }

      return true;
    });
  }, [clientServices, filterService, filterSite, filterStartDate, filterEndDate]);

  const clearFilters = () => {
    setFilterService('all');
    setFilterSite('all');
    setFilterStartDate('');
    setFilterEndDate('');
  };

  const hasActiveFilters =
    filterService !== 'all' ||
    filterSite !== 'all' ||
    filterStartDate !== '' ||
    filterEndDate !== '';

  return (
    <div>
      <div className={styles.pageHeader}>
        <Text className={styles.pageTitle}>Client Services</Text>
        <Text className={styles.pageSubtitle}>
          {loading
            ? 'Loading...'
            : hasActiveFilters
              ? `${filteredClientServices.length} of ${clientServices.length} services`
              : `${clientServices.length} services`}
        </Text>
      </div>

      <div className={styles.filterBar}>
        <div style={{ width: '300px' }}>
          <SearchableDropdown
            placeholder="Search client..."
            options={clients.map(client => ({
              id: client.id,
              label: client.client_name,
            }))}
            selectedValue={selectedClient || ''}
            onSelect={value => setSelectedClient(value)}
          />
        </div>
      </div>

      {selectedClient && (
        <>
          <TableCommandBar
            onAdd={() =>
              navigate('/account/add-client-service', { state: { clientId: selectedClient } })
            }
            addLabel="Add Service"
          />

          <div className={styles.filters}>
            <div className={styles.filterHeader}>
              <Text className={styles.filterTitle}>
                <FilterRegular />
                Filters
              </Text>
              {hasActiveFilters && (
                <Button
                  appearance="subtle"
                  size="small"
                  icon={<DismissRegular />}
                  onClick={clearFilters}
                >
                  Clear Filters
                </Button>
              )}
            </div>
            <div className={styles.filterRow}>
              <div className={styles.filterDropdown}>
                <SearchableDropdown
                  placeholder="Filter by Service"
                  selectedValue={filterService}
                  onSelect={value => setFilterService(value)}
                  options={[
                    { id: 'all', label: 'All Services' },
                    ...uniqueServices.map(s => ({
                      id: s.id.toString(),
                      label: s.service_name,
                    })),
                  ]}
                />
              </div>
              <div className={styles.filterDropdown}>
                <SearchableDropdown
                  placeholder="Filter by Site"
                  selectedValue={filterSite}
                  onSelect={value => setFilterSite(value)}
                  options={[
                    { id: 'all', label: 'All Sites' },
                    ...uniqueSites.map(s => ({
                      id: s.id.toString(),
                      label: s.site_name,
                    })),
                  ]}
                />
              </div>
              <div className={styles.filterDate}>
                <Field label="Start Date">
                  <Input
                    type="date"
                    contentBefore={<CalendarRegular />}
                    value={filterStartDate}
                    onChange={e => setFilterStartDate(e.target.value)}
                  />
                </Field>
              </div>
              <div className={styles.filterDate}>
                <Field label="End Date">
                  <Input
                    type="date"
                    contentBefore={<CalendarRegular />}
                    value={filterEndDate}
                    onChange={e => setFilterEndDate(e.target.value)}
                  />
                </Field>
              </div>
            </div>
          </div>
        </>
      )}

      <ContentCard noPadding>
        {!selectedClient ? (
          <div className={styles.emptyState}>
            <Text size={500} weight="semibold" style={{ display: 'block' }}>
              Select a client
            </Text>
            <Text style={{ marginTop: '8px', display: 'block' }}>
              Choose a client to view their services
            </Text>
          </div>
        ) : filteredClientServices.length === 0 ? (
          <div className={styles.emptyState}>
            <Text size={500} weight="semibold" style={{ display: 'block' }}>
              {hasActiveFilters ? 'No matching services' : 'No services assigned'}
            </Text>
            <Text style={{ marginTop: '8px', display: 'block' }}>
              {hasActiveFilters
                ? 'Try adjusting your filters'
                : 'Click "Add Service" to assign services to this client'}
            </Text>
          </div>
        ) : (
          <table className={styles.table}>
            <thead className={styles.tableHeader}>
              <tr>
                <th className={styles.th}>Service</th>
                <th className={styles.th}>Site</th>
                <th className={styles.th}>Quantity</th>
                <th className={styles.th}>Description</th>
                <th className={styles.th}>Start Date</th>
                <th className={styles.th}>End Date</th>
                <th className={styles.th} style={{ width: '80px' }}>
                  Actions
                </th>
              </tr>
            </thead>
            <tbody>
              {filteredClientServices.map((service, idx) => (
                <tr key={idx} className={styles.tr}>
                  <td className={styles.td}>
                    <Badge appearance="filled" color="brand">
                      {service.service_name || '—'}
                    </Badge>
                  </td>
                  <td className={styles.td}>{service.site_name || '—'}</td>
                  <td className={styles.td}>{service.quantity || '—'}</td>
                  <td className={styles.td}>{service.description || '—'}</td>
                  <td className={styles.td}>
                    {service.start_date ? fullDate(service.start_date) : '—'}
                  </td>
                  <td className={styles.td}>
                    {service.end_date ? fullDate(service.end_date) : '—'}
                  </td>
                  <td className={styles.td}>
                    <Menu>
                      <MenuTrigger>
                        <Button
                          icon={<MoreHorizontalRegular />}
                          appearance="subtle"
                          className={styles.actionButton}
                        />
                      </MenuTrigger>
                      <MenuPopover>
                        <MenuList>
                          <MenuItem
                            icon={<EditRegular />}
                            onClick={() =>
                              navigate('/account/add-client-service', {
                                state: { clientService: service, clientId: selectedClient },
                              })
                            }
                          >
                            Edit
                          </MenuItem>
                          <MenuItem
                            icon={<DeleteRegular />}
                            onClick={() => {
                              setSelectedService(service);
                              setDeleteDialogOpen(true);
                            }}
                          >
                            Delete
                          </MenuItem>
                        </MenuList>
                      </MenuPopover>
                    </Menu>
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        )}
      </ContentCard>

      {/* Delete Confirmation Dialog */}
      <Dialog open={deleteDialogOpen} onOpenChange={(e, data) => setDeleteDialogOpen(data.open)}>
        <DialogSurface
          style={{
            maxWidth: '400px',
            position: 'fixed',
            top: '50%',
            left: '50%',
            transform: 'translate(-50%, -50%)',
          }}
        >
          <DialogBody>
            <DialogTitle>Delete Service</DialogTitle>
            <DialogContent>
              Are you sure you want to delete this service assignment? This action cannot be undone.
            </DialogContent>
            <DialogActions>
              <Button appearance="secondary" onClick={() => setDeleteDialogOpen(false)}>
                Cancel
              </Button>
              <Button appearance="primary" onClick={handleDeleteService}>
                Delete
              </Button>
            </DialogActions>
          </DialogBody>
        </DialogSurface>
      </Dialog>
    </div>
  );
};

export default ClientServicesFluentUI;
